import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AssistantError,
  WEBSITE_REPLY_LIMIT,
  fetchAssistantStatus,
  streamAssistantChat,
  type AssistantChatMessage,
  type AssistantSource,
  type AssistantStatus,
} from '@/lib/assistant';

/**
 * 官网 AI 客服悬浮球与小窗。
 *
 * 状态接口不可用或后台关闭时整块不渲染。回答按纯文本展示，不使用 HTML 注入。
 */

interface ChatItem extends AssistantChatMessage {
  id: string;
  sources?: AssistantSource[];
  /** 每次收到的增量单独成段，渲染时逐段淡入。 */
  parts?: string[];
}

function nextId(): string {
  return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 服务端已剥掉开头空白；这里再兜一层，避免上游变更后小窗顶部出现空行。 */
function appendDelta(current: string, delta: string): string {
  if (current !== '') return current + delta;
  return delta.replace(/^[\s\uFEFF]+/, '');
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
      <path d="M4.5 5.5h15v10h-8l-4.5 4v-4H4.5z" strokeLinejoin="round" />
      <path d="M8 9h8M8 12h5" strokeLinecap="round" />
    </svg>
  );
}

export function AssistantWidget() {
  const [status, setStatus] = useState<AssistantStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [messages, setMessages] = useState<ChatItem[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  function openPanel() {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setClosing(false);
    setOpen(true);
    if (limitReached) setLimitModalOpen(true);
  }

  function closePanel() {
    if (!open || closing) return;
    setLimitModalOpen(false);
    setClosing(true);
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
      closeTimer.current = null;
    }, 170);
  }

  useEffect(() => {
    const controller = new AbortController();
    void fetchAssistantStatus(controller.signal).then(setStatus);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePanel();
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
    // closePanel 只依赖当前开关状态，跟随 open 重新绑定即可。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closing]);

  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, sending]);

  const replies = messages.filter(
    (message) => message.role === 'assistant' && message.content.trim() !== '',
  ).length;

  useEffect(() => {
    if (replies >= WEBSITE_REPLY_LIMIT) {
      setLimitReached(true);
      setLimitModalOpen(true);
    }
  }, [replies]);

  async function send(content: string) {
    const text = content.trim();
    if (text === '' || sending || limitReached) return;

    const history: AssistantChatMessage[] = [
      ...messages.map((message) => ({ role: message.role, content: message.content })),
      { role: 'user', content: text },
    ];
    const assistantId = nextId();
    setMessages((current) => [
      ...current,
      { id: nextId(), role: 'user', content: text },
      { id: assistantId, role: 'assistant', content: '' },
    ]);
    setInput('');
    setError(null);
    setSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamAssistantChat(
        history,
        {
          onSources: (sources) =>
            setMessages((current) =>
              current.map((message) => (message.id === assistantId ? { ...message, sources } : message)),
            ),
          onDelta: (delta) =>
            setMessages((current) =>
              current.map((message) => {
                if (message.id !== assistantId) return message;
                const content = appendDelta(message.content, delta);
                const added = content.slice(message.content.length);
                return { ...message, content, parts: [...(message.parts ?? []), added] };
              }),
            ),
        },
        controller.signal,
      );
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return;
      if (caught instanceof AssistantError && caught.code === 'assistant_limit_reached') {
        setLimitReached(true);
        setLimitModalOpen(true);
      } else {
        setError(caught instanceof AssistantError ? caught.message : 'AI 客服暂时不可用，请稍后再试');
      }
      setMessages((current) =>
        current.map((message) =>
          message.id === assistantId && message.content === ''
            ? { ...message, content: '（本次没有生成回答）' }
            : message,
        ),
      );
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }

  function stop() {
    abortRef.current?.abort();
    setSending(false);
  }

  if (!status?.available) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? closePanel() : openPanel())}
        aria-label={open ? '收起 AI 客服' : '打开 AI 客服'}
        aria-expanded={open}
        className="fixed right-4 bottom-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-grad-cta text-grad-fg shadow-[0_10px_30px_-8px_rgb(46_95_232/0.8)] transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:right-6 sm:bottom-6 sm:h-14 sm:w-14"
      >
        {!open ? (
          <span className="assistant-fab-pulse pointer-events-none absolute inset-0 rounded-full bg-brand/40" aria-hidden="true" />
        ) : null}
        <span className="relative flex items-center justify-center">
          {open ? (
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
            </svg>
          ) : (
            <ChatIcon />
          )}
        </span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="AI 客服"
          className={`assistant-window ${closing ? 'assistant-window-closing' : 'assistant-window-open'} fixed inset-x-3 bottom-20 z-40 flex h-[min(68vh,560px)] flex-col overflow-hidden rounded-card border border-line-strong bg-surface-1 shadow-[0_24px_60px_-20px_rgb(30_60_110/0.45)] sm:inset-x-auto sm:right-6 sm:bottom-24 sm:w-[390px]`}
        >
          <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-fg">小雨 · Havorix™ 智能客服</p>
              <p className="mt-0.5 text-[11px] text-fg-subtle">回答由 AI 生成，重要信息请以页面或工单为准</p>
            </div>
            <button
              type="button"
              onClick={closePanel}
              aria-label="关闭"
              className="rounded-btn p-1 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
              </svg>
            </button>
          </header>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {status.greeting ? (
              <div className="max-w-[92%] rounded-card rounded-tl-sm bg-surface-2 px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap text-fg">
                {status.greeting}
              </div>
            ) : null}

            {messages.map((message, index) =>
              message.role === 'user' ? (
                <div key={message.id} className="flex justify-end">
                  <div className="max-w-[88%] rounded-card rounded-tr-sm bg-grad-cta px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap text-grad-fg">
                    {message.content}
                  </div>
                </div>
              ) : (
                <div key={message.id} className="max-w-[92%]">
                  <div className="rounded-card rounded-tl-sm bg-surface-2 px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap text-fg">
                    {message.parts && message.parts.length > 0
                      ? message.parts.map((part, partIndex) => (
                          <span key={partIndex} className="assistant-delta">
                            {part}
                          </span>
                        ))
                      : message.content || (sending ? <span className="text-fg-subtle">正在思考…</span> : '')}
                    {sending && index === messages.length - 1 && message.content !== '' ? (
                      <span className="assistant-caret" aria-hidden="true" />
                    ) : null}
                  </div>
                  {message.sources && message.sources.length > 0 ? (
                    <p className="mt-1 text-[11px] text-fg-subtle">
                      参考：{message.sources.map((source) => source.title).join('、')}
                    </p>
                  ) : null}
                </div>
              ),
            )}

            {limitReached ? (
              <div className="rounded-card bg-surface-2 px-3 py-2 text-[12px] leading-relaxed text-fg-muted">
                本次对话已达上限，想了解更多可
                <Link to="/contact" className="mx-1 text-brand underline underline-offset-2">
                  联系我们
                </Link>
                。
              </div>
            ) : null}

            {!limitReached && messages.length === 0 && status.suggestedQuestions.length > 0 ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {status.suggestedQuestions.map((question) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => void send(question)}
                    className="rounded-full border border-line-strong px-2.5 py-1 text-[12px] text-fg-muted transition-colors hover:border-brand hover:text-brand"
                  >
                    {question}
                  </button>
                ))}
              </div>
            ) : null}

            {error ? (
              <div className="rounded-btn bg-brand-soft px-3 py-2 text-[12px] text-fg">{error}</div>
            ) : null}
          </div>

          <form
            className="border-t border-line px-3 py-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!limitReached) void send(input);
            }}
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                rows={1}
                maxLength={2_000}
                disabled={limitReached}
                placeholder={
                  limitReached ? '本次对话已达上限，请前往联系我们' : '输入问题，Enter 发送，Shift+Enter 换行'
                }
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    if (!limitReached) void send(input);
                  }
                }}
                className="max-h-28 min-h-9 flex-1 resize-none rounded-btn border border-line-strong bg-surface-1 px-3 py-2 text-[13px] leading-relaxed text-fg placeholder:text-fg-subtle focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:bg-surface-2"
              />
              {sending ? (
                <button
                  type="button"
                  onClick={stop}
                  className="h-9 shrink-0 rounded-btn border border-line-strong px-3 text-[13px] text-fg-muted transition-colors hover:text-fg"
                >
                  停止
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={input.trim() === '' || limitReached}
                  aria-label="发送"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-btn bg-grad-cta text-grad-fg transition-[filter] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true" fill="currentColor">
                    <path d="M2.5 3.2 17.3 10 2.5 16.8l2-6.1 7.6-.7-7.6-.7-2-6.1Z" />
                  </svg>
                </button>
              )}
            </div>
          </form>
        </div>
      ) : null}

      {open && limitModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="本次对话已达上限"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
        >
          <div className="assistant-modal-open w-full max-w-sm rounded-card border border-line-strong bg-surface-1 p-5 shadow-[0_24px_60px_-20px_rgb(30_60_110/0.45)]">
            <p className="text-[15px] leading-relaxed text-fg">
              小雨有些困啦，想了解更多？
              <Link
                to="/contact"
                className="ml-1 text-brand underline underline-offset-2"
              >
                点这里联系我们
              </Link>
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setLimitModalOpen(false)}
                className="rounded-btn border border-line-strong px-3 py-1.5 text-[13px] text-fg-muted transition-colors hover:text-fg"
              >
                稍后再说
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
